import { useCallback, useState } from 'react';

type SearchBox = {
  // Which place the box belongs to, and which of its boxes there: a move or an opened match
  // starts the next one.
  key: string;
  location: string;
  run: number;
  value: string;
};

const keyOf = (location: string, run: number) => `${location}:${run}`;

/**
 * The browser's search box. A term belongs to the box it was typed in: moving to another folder
 * or space, or opening a match, empties it for good, so coming back never brings the old term
 * back. `searchKey` changes with every new box, so the input is remounted empty too.
 */
export const useBrowserSearch = (location: string) => {
  const [box, setBox] = useState<SearchBox>({
    key: keyOf(location, 0),
    location,
    run: 0,
    value: '',
  });

  // Reset while rendering, before the old term can reach a query for the new place.
  if (box.location !== location) {
    const run = box.run + 1;

    setBox({ key: keyOf(location, run), location, run, value: '' });
  }

  const { key: searchKey } = box;

  // A debounced term from a box already left behind lands nowhere.
  const onSearch = useCallback(
    (value: string) =>
      setBox((current) => (current.key === searchKey ? { ...current, value } : current)),
    [searchKey],
  );

  const clearSearch = useCallback(
    () =>
      setBox((current) => {
        const run = current.run + 1;

        return { ...current, key: keyOf(current.location, run), run, value: '' };
      }),
    [],
  );

  return {
    clearSearch,
    onSearch,
    search: box.location === location ? box.value : '',
    searchKey,
  };
};
